import { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import Select from "react-select";
import {
  Button,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";

import { t } from "i18next";
import Draggable from "react-draggable";
import swal from "sweetalert";
import {
  LoadingSpinner,
  convertToDateString,
  convertUTCToLocalTime,
} from "../../constants/const";
import { ApiFinance } from "../config/finance/ApiFinance";
import { GetLanguageString } from "../helper/Components";
import { GetSelectedLanguage } from "../helper/Method";
import { ApiFund } from "./ApiFund";

const EditFund = ({
  setModal,
  memberName,
  setMemberFunds,
  editValue,
  unitId,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const toggle = () => setModal(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [fundValues, setFundValues] = useState({});
  const [serverError, setServerError] = useState("");
  const [errors, setErrors] = useState({
    date: "",
    month: "",
    year: "",
    receiptNumber: "",
    amount: "",
  });
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [infaqHeads, setInfaqHeads] = useState([]);
  const [selectedInfaqHead, setSelectedInfaqHead] = useState(null);

  const handleDrag = (e, ui) => {
    const { x, y } = position;
    setPosition({ x: x + ui.deltaX, y: y + ui.deltaY });
  };

  useEffect(() => {
    const currentDate = new Date();
    const tempYear = currentDate.getFullYear();
    const tempMonth = currentDate.getMonth() + 1;
    const loadData = async () => {
      try {
        setLoading(true);
        const fundRes = await ApiFund().getById(editValue);
        const fund = fundRes.data;
        setFundValues({
          ...fund,
          date: fund.date ? convertToDateString(fund.date) : null,
          year: fund.year || tempYear,
          month: fund.month || tempMonth,
        });
        setSelectedDate(
          fund.date ? new Date(convertUTCToLocalTime(fund.date)) : null,
        );
        
        const headRes = await ApiFinance().GetInfaqHeadsList();


        if (headRes?.data?.length > 0) {
          setInfaqHeads(headRes.data);
          const selectedHead = headRes.data.find(
            (item) => item.value === fund.codeId,
          );
          if (selectedHead) {
            setSelectedInfaqHead(selectedHead);
            setFundValues((prev) => ({
              ...prev,
              codeId: selectedHead.value,
            }));
          }
        }

        setLoading(false);
      } catch (err) {
        console.error("Error:", err);
        setLoading(false);
      }
    };

    if (editValue && unitId) loadData();
  }, [editValue, unitId]);

  const handleInfaqHeadChange = (selected) => {
    setSelectedInfaqHead(selected);

    setFundValues((prev) => ({
      ...prev,
      codeId: selected ? selected.value : "",
    }));

    setErrors((prev) => ({
      ...prev,
      selectedInfaqHeadErrorMsg: selected
        ? ""
        : "Please select an Account Head",
    }));
  };

  const validate = () => {
    let temp = {};
    temp.month = fundValues.month ? (
      ""
    ) : (
      <GetLanguageString props="funds_add_fund_select_month_error" />
    );
    temp.year = fundValues.year ? (
      ""
    ) : (
      <GetLanguageString props="funds_add_fund_select_year_error" />
    );
    temp.receiptNumber = fundValues.receiptNumber ? (
      ""
    ) : (
      <GetLanguageString props="common_field_required_error" />
    );
    temp.date = selectedDate ? (
      ""
    ) : (
      <GetLanguageString props="funds_add_fund_date_paid_error" />
    );
    temp.amount = fundValues.amount ? (
      ""
    ) : (
      <GetLanguageString props="funds_add_fund_amount_paid_error" />
    );

    setErrors({
      ...temp,
    });

    return Object.values(temp).every((x) => x === "");
  };

  const handleInputChangeDate = (value) => {
    setSelectedDate(value);
    setErrors((prev) => ({
      ...prev,
      date: "",
    }));
  };

  const handleReceiptNumber = (e) => {
    const { value } = e.target;
    setFundValues({
      ...fundValues,
      receiptNumber: value,
    });
    setErrors((prev) => ({
      ...prev,
      receiptNumber: "",
    }));
  };

  // It wil handle change in Amount
  const handleAmountChange = (e) => {
    let { value } = e.target;
    value = value.replace(/[^0-9.]/g, "");
    setFundValues({
      ...fundValues,
      amount: value,
    });
    setErrors((prev) => ({
      ...prev,
      amount: "", // only remove amount error
    }));
  };

  const handleSubmit = () => {
    if (validate()) {
      setLoading(true);
      fundValues.date = convertToDateString(selectedDate);
      fundValues.month = selectedDate ? selectedDate.getMonth() + 1 : fundValues.month;
      fundValues.year = selectedDate ? selectedDate.getFullYear() : fundValues.year;
      ApiFund()
        .update(editValue, fundValues)
        .then(() => {
          swal({
            title: t("member_member_detail_fund_edited"),
            className: GetSelectedLanguage() === "en" ? "" : "ur",
            icon: "success",
            buttons: "OK",
          });
          if (
            typeof setMemberFunds === "function" &&
            fundValues.unitId &&
            fundValues.memberId
          ) {
            ApiFund()
              .getList(fundValues.unitId, fundValues.memberId)
              .then((res) => {
                setMemberFunds(res.data);
                setModal(false);
                setLoading(false);
              })
              .catch((err) => {
                console.log(err);
                setLoading(false);
              });
          }
          if (typeof onSuccess === "function") {
            onSuccess();
          }
          setModal(false);
        })
        .catch((err) => {
          if (err.response.data.length < 100) {
            setServerError(err.response.data);
          }
          setModal(false);
          swal({
            title: err,
            icon: "error",
            buttons: "OK",
          });
          console.log(err);
          setLoading(false);
        })
        .finally(() => {
          setLoading(false);
          setModal(false);
        });
    }
  };

  return (
    <div>
      <Draggable position={position} onDrag={handleDrag} cancel=".no-drag">
        <Modal isOpen className="inmodal" autoFocus={false}>
          <ModalHeader className="modal-title" tag="h4">
            {<GetLanguageString props="edit_infaq" />}
            <p className="ur">{memberName}</p>
          </ModalHeader>
          {!loading ? (
            <Form>
              <ModalBody>
                {serverError && <h4 className="text-danger">{serverError}</h4>}
                <Row>
                  <Col md="6">
                    <FormGroup>
                      <Label className="col-form-label">
                        {<GetLanguageString props="funds_add_fund_date_paid" />}
                      </Label>
                      <DatePicker
                        placeholderText="dd/mm/yyyy"
                        className="form-control no-drag"
                        dateFormat="dd/MM/yyyy"
                        selected={selectedDate}
                        maxDate={new Date()}
                        onChange={handleInputChangeDate}
                      />
                      {errors.date && (
                        <h4 className="text-error">{errors.date}</h4>
                      )}
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label className="col-form-label">
                        {<GetLanguageString props="funds_receipt_number" />}
                      </Label>
                      <Input
                        autoFocus
                        type="text"
                        className="no-drag"
                        value={fundValues.receiptNumber}
                        onChange={handleReceiptNumber}
                      />
                      {errors.receiptNumber && (
                        <h4 className="text-error">{errors.receiptNumber}</h4>
                      )}
                    </FormGroup>
                  </Col>

                  {/* INFAQ HEAD */}
                  <Col md="12">
                    <FormGroup>
                      <Label className="col-form-label">
                        <GetLanguageString props="fund_account_head" />
                      </Label>
                      <Select
                        options={infaqHeads}
                        value={selectedInfaqHead}
                        placeholder="Select bank account"
                        onChange={handleInfaqHeadChange}
                        className="basic-single no-drag"
                        classNamePrefix="select"
                      />

                      {errors.selectedInfaqHeadErrorMsg && (
                        <h4 className="text-error">
                          {errors.selectedInfaqHeadErrorMsg}
                        </h4>
                      )}
                    </FormGroup>
                  </Col>
                  {/* Add Amount for the fund */}
                  <Col md="6">
                    <FormGroup>
                      <Label className="col-form-label">
                        {<GetLanguageString props="funds_add_fund_amount" />}
                      </Label>
                      <Input
                        autoFocus
                        type="text"
                        className="no-drag"
                        value={fundValues.amount}
                        onChange={handleAmountChange}
                      />
                      {errors.amount && (
                        <h4 className="text-error">{errors.amount}</h4>
                      )}
                    </FormGroup>
                  </Col>
                </Row>
              </ModalBody>
              <ModalFooter>
                <Button color="white" onClick={toggle}>
                  {<GetLanguageString props="common_cancel" />}
                </Button>
                <Button color="primary" onClick={handleSubmit}>
                  {<GetLanguageString props="common_save_changes" />}
                </Button>
              </ModalFooter>
            </Form>
          ) : (
            LoadingSpinner()
          )}
        </Modal>
      </Draggable>
    </div>
  );
};

export default EditFund;
